import * as React from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import breakpoints, { forDesktop, forIE, forIPhone5, forTablet } from '@westwing/ui-kit/breakPoints';
import { body3, caption1 } from '@westwing/ui-kit/typography';
import GiftBoxIcon from '@westwing/ui-kit/Icons/GiftBoxIcon';
import { paddedMaxScreenWidth } from '../GlobalStyles/theme';
import { showOverlay } from 'Client/redux/overlays/actions';
import useConfig from 'Client/hooks/useConfig';
import useGa from 'Client/hooks/useGa';
import useShowPromoBarBanner from 'Client/hooks/flags/useShowPromoBarBanner';
import { HeaderModeType, useLayoutByPage } from 'Client/hooks/useLayoutByPage';
import { ShopToClubShownLabel } from 'Common/ga/shopToClub';
import useBellaTranslator from 'Client/hooks/useTranslator';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { EventDispatcher } from 'Common/events';
import { DisplayActions, OverlayIds } from 'Common/appshell_types';

const StyledWrapper = styled.div<{ isSticky: boolean; p100IncreasedDesktopPageWidth: boolean }>`
    position: ${({ isSticky }) => (isSticky ? 'sticky' : 'static')};

    height: 45px;
    ${forTablet`
        height: 57px;
    `}
    background: ${({ theme }) => theme.colors.white};
    display: grid;
    grid-template-columns: auto 40px;
    cursor: pointer;

    top: ${({ theme }) => theme.oneHeaderModuleHeight}px;
    ${forDesktop`
        top: 0px;
    `}

    z-index: ${({ theme }) => theme.zIndexBelowContentOverlay};
    max-width: ${breakpoints.breakpointSemiWideScreens};
    ${({ p100IncreasedDesktopPageWidth }) =>
        p100IncreasedDesktopPageWidth &&
        css`
            max-width: ${paddedMaxScreenWidth};
        `}
    margin: auto;

    ${forIE`
        display: none;
    `}
`;

const ClubBannerInnerDiv = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.iconCritical};
    height: calc(100% - 14px);
    margin: 7px;
    display: flex;
    justify-content: center;
    grid-row: 1;
    grid-column: 1 / span 2;
`;

const BannerText = styled.div`
    color: ${({ theme }) => theme.colors.iconCritical};
    ${caption1}
    ${forIPhone5`
        font-size: 10px;
    `}
    ${forTablet`
        ${body3}
        padding-left: 10px;
        font-size: 15px;
        line-height: 20px;
    `}
    ${forDesktop`
        padding-left: 17px;
        font-size: 17px;
    `}
`;

const GiftIcon = styled(GiftBoxIcon)`
    height: 18px;
    display: none;
    path {
        fill: ${({ theme }) => theme.colors.iconCritical};
    }
    ${forTablet`
        display: block;
        height: 20px;
    `}
    ${forDesktop`
        height: auto;
    `}
`;

const ClubBannerContent = styled.div`
    grid-row: 1;
    grid-column: 2;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ClubBarBanner = () => {
    const dispatch = useDispatch();
    const gaTracker = useGa();
    const t = useBellaTranslator();
    const { p100IncreasedDesktopPageWidth, p100SignupOverlayMicroFrontend } = useFeatureToggle();
    const config = useConfig();
    const showClubHeader = useShowPromoBarBanner();
    const { headerMode } = useLayoutByPage();

    const openClubRegistration = () => {
        if (p100SignupOverlayMicroFrontend) {
            const eventDispatcher = EventDispatcher();
            eventDispatcher.updateOverlay(DisplayActions.SHOW, config.shopToClubOverlayId as OverlayIds, {});
        } else {
            dispatch(
                showOverlay(config.shopToClubOverlayId, {
                    isLogin: false,
                    data: { trackingLabel: ShopToClubShownLabel.Banner },
                })
            );
        }
        gaTracker.shopToClub.trackHeaderBannerClicked();
    };

    React.useEffect(() => {
        if (showClubHeader) {
            gaTracker.shopToClub.trackHeaderBannerShown();
        }
    }, [gaTracker.shopToClub, showClubHeader]);

    if (!showClubHeader) {
        return <div />;
    }

    return (
        <StyledWrapper
            isSticky={headerMode === HeaderModeType.DEFAULT}
            onClick={openClubRegistration}
            p100IncreasedDesktopPageWidth={!!p100IncreasedDesktopPageWidth}
        >
            <ClubBannerInnerDiv>
                <ClubBannerContent>
                    <GiftIcon />
                    <BannerText>{t('Header promo banner')}</BannerText>
                </ClubBannerContent>
            </ClubBannerInnerDiv>
        </StyledWrapper>
    );
};

export default ClubBarBanner;
